import { Badge, Text, Toggle } from '@ui/themed';
import { t } from 'i18next';
import type { ReactNode } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { AccountSectionConfiguration, AccountSectionId } from '../types/spark-options';
import {
  type AccountSectionItem,
  getAccountSectionTitle,
  toAccountSectionItems,
  toSparkAccountSectionConfiguration,
} from '../utils/account-sections';
import { SortableList } from './sortable-list';

const EDITOR =
  'features:storefront_options.components.account_sections_editor' satisfies I18nPrefix;

function AccountSectionRow({
  item,
  onChange,
  disabledReason,
  forceHidden,
}: {
  item: AccountSectionItem;
  onChange: (item: AccountSectionItem) => void;
  disabledReason?: ReactNode;
  forceHidden?: boolean;
}) {
  const isDisabled = Boolean(disabledReason);
  const isVisible = forceHidden ? false : !item.hidden;
  const title = getAccountSectionTitle(item.id);

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Text truncate>{title}</Text>
          <Badge variant={isVisible ? 'green' : 'default'} size="sm">
            {t(`${EDITOR}.${isVisible ? 'visible_badge' : 'hidden_badge'}`)}
          </Badge>
        </div>
        {disabledReason && (
          <Text size="sm" variant="secondary">
            {disabledReason}
          </Text>
        )}
      </div>
      <Toggle
        checked={isVisible}
        disabled={isDisabled}
        onChange={(checked) => {
          if (isDisabled) return;
          onChange({ ...item, hidden: !checked });
        }}
      />
    </div>
  );
}

export function AccountSectionsEditor({
  values,
  onChange,
  disabledSectionReasons,
  forcedHiddenSections = [],
}: {
  values: AccountSectionConfiguration | undefined;
  onChange: (values: AccountSectionConfiguration) => void;
  disabledSectionReasons?: Partial<Record<AccountSectionId, ReactNode>>;
  forcedHiddenSections?: AccountSectionId[];
}) {
  const items = toAccountSectionItems(values);

  function emit(nextItems: AccountSectionItem[]) {
    onChange(toSparkAccountSectionConfiguration(nextItems));
  }

  function updateAt(index: number, next: AccountSectionItem) {
    emit(items.map((item, i) => (i === index ? next : item)));
  }

  return (
    <div className="flex flex-col gap-3">
      {items.length === 0 && (
        <Text size="sm" variant="secondary">
          {t(`${EDITOR}.empty`)}
        </Text>
      )}

      <SortableList
        items={items}
        getKey={(item) => item.id}
        onReorder={emit}
        renderItem={(item, index) => (
          <AccountSectionRow
            item={item}
            disabledReason={disabledSectionReasons?.[item.id]}
            forceHidden={forcedHiddenSections.includes(item.id)}
            onChange={(next) => updateAt(index, next)}
          />
        )}
      />
    </div>
  );
}
